import { useMemo } from "react";
import { useTranslation } from "react-i18next";

/**
 * Select option dictionaries for the job offer form (RekruterAI).
 * Each enum lists the `value` contracts sent to the backend; display labels
 * are resolved via i18n through `useJobOfferEnums()` (translations live in
 * src/i18n/locales/{lng}/jobOffer.json under "enums.{ENUM_NAME}.{value}").
 *
 * NOTE: enums marked "// TODO: confirm with PM" had no values given in the source
 * spec — the listed values are a proposal pending confirmation.
 */

const ENUM_VALUES = {
    WORK_MODE: ["stationary", "hybrid", "remote"],
    SENIORITY_LEVEL: ["junior", "mid", "senior", "manager"],
    OFFER_STATUS: ["draft", "active", "inactive"],
    EMPLOYMENT_FORM_TYPE: [
        "employmentContract",
        "mandateContract",
        "contractForSpecificWork",
        "b2b",
        "contract",
        "temporaryWork",
        "other",
    ],
    SALARY_MODEL_TYPE: [
        "hourlyRate",
        "fixedMonthly",
        "pieceRate",
        "pieceRatePlusBase",
        "basePlusBonus",
        "basePlusCommission",
        "commission",
        "projectBased",
        "other",
    ],
    // TODO: confirm with PM — currency list wasn't specified in the source spec
    CURRENCY: ["PLN", "EUR", "USD"],
    PIECE_RATE_UNIT: ["piece", "course", "order", "kilogram", "meter", "service", "project", "other"],
    // TODO: confirm with PM — B2B settlement period values weren't specified in the source spec
    SETTLEMENT_PERIOD: ["weekly", "biweekly", "monthly", "other"],
    BONUS_TYPE: [
        "monthly",
        "quarterly",
        "annual",
        "discretionary",
        "commission",
        "attendance",
        "quality",
        "safety",
        "other",
    ],
    ALLOWANCE_TYPE: [
        "night",
        "shift",
        "overtime",
        "businessTrip",
        "specialConditions",
        "carAllowance",
        "remoteWorkAllowance",
        "other",
    ],
    WORKLOAD_TYPE: ["fullTime", "threeQuarterTime", "halfTime", "partTime", "seasonal", "project"],
    WORK_SYSTEM: ["oneShift", "twoShift", "threeShift", "fourBrigade", "taskBased"],
    OVERTIME_FREQUENCY: ["none", "occasional", "fewTimesAMonth", "regular"],
    OVERTIME_SETTLEMENT: ["paidExtra", "timeOff", "both", "other"],
    DOCUMENT_TYPE: [
        "cv",
        "drivingLicense",
        "professionalLicense",
        "certificate",
        "sanitaryBook",
        "confirmation",
        "portfolio",
        "other",
    ],
    REQUIREMENT_STATUS: ["required", "optional", "niceToHave"],
    CRITICALITY: ["critical", "important", "additional"],
    SKILL_CATEGORY: ["technical", "soft", "language", "sales", "industry", "managerial", "other"],
    RATING_SCALE: ["scale1to5", "scale1to10", "levelWords", "yesNo"],
    CONFIRMATION_TYPE: ["certificate", "course", "portfolio", "references", "workExperience", "none"],
    CUSTOM_QUESTION_ANSWER_TYPE: ["text", "yesNo", "number", "date", "scale", "singleChoice", "multiChoice"],
    CONVERSATION_STYLE: ["formal", "professional", "friendly", "casual"],
    CONVERSATION_LANGUAGE: ["pl", "en", "de", "ru", "uk", "es", "fr"],
    RECOMMENDATION_STATUS: [
        "veryGoodMatch",
        "goodMatch",
        "partialMatch",
        "lowMatch",
        "rejected",
        "requiresManualReview",
    ],
};

/**
 * Returns translated { value, label } option arrays for every job offer enum,
 * keyed the same as the previous static exports (WORK_MODE, SENIORITY_LEVEL, ...).
 */
export function useJobOfferEnums() {
    const { t } = useTranslation("jobOffer");

    return useMemo(() => {
        const result = {};
        for (const [enumName, values] of Object.entries(ENUM_VALUES)) {
            result[enumName] = values.map((value) => ({
                value,
                label: t(`enums.${enumName}.${value}`),
            }));
        }
        return result;
    }, [t]);
}
